import React, { useState, useEffect, useMemo } from 'react';
import { getPaymentStatusColor } from '../../utils/statusBadge';
import {
  Box,
  Button,
  useToast,
  Text,
  HStack,
  Heading,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  InputGroup,
  InputLeftElement,
  Input,
  Container,
  Card,
  CardBody,
  Flex,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  VStack,
  Spinner,
  Icon,
} from '@chakra-ui/react';
import { SearchIcon, ChevronDownIcon, DeleteIcon, AddIcon } from '@chakra-ui/icons';
import { FaFileInvoiceDollar, FaCheckCircle, FaUndo, FaEye, FaLock, FaTrash, FaUserPlus, FaFilePdf, FaFileCsv } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';
import { effectiveDrawPrice, flooredPrice, MIN_DRAW_PRICE } from '../../utils/invoicePricing';
import { downloadInvoicePDF, buildInvoiceCSV, downloadText } from '../../utils/invoiceExport';

const DEFAULT_ADMIN_FEE = 5;

function effectiveAdminFee(sub) {
  const v = sub.admin_fee;
  if (v != null && v !== '' && !Number.isNaN(parseFloat(v)) && parseFloat(v) > 0) return parseFloat(v);
  return DEFAULT_ADMIN_FEE;
}

const InvoicesList = ({ isActive }) => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [batches, setBatches] = useState({});
  const [submissionsByBatch, setSubmissionsByBatch] = useState({});
  const [phlebotomists, setPhlebotomists] = useState({});
  const [labs, setLabs] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [markingPaidId, setMarkingPaidId] = useState(null);
  const [unmarkingPaidId, setUnmarkingPaidId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const toast = useToast();
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isMarkPaidOpen, onOpen: onMarkPaidOpen, onClose: onMarkPaidClose } = useDisclosure();
  const { isOpen: isUnmarkPaidOpen, onOpen: onUnmarkPaidOpen, onClose: onUnmarkPaidClose } = useDisclosure();
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [invoiceToMarkPaid, setInvoiceToMarkPaid] = useState(null);
  const [invoiceToUnmarkPaid, setInvoiceToUnmarkPaid] = useState(null);
  const [viewSubmissions, setViewSubmissions] = useState([]);
  const [viewLoading, setViewLoading] = useState(false);
  const [removingPatientId, setRemovingPatientId] = useState(null);
  const [candidatePatients, setCandidatePatients] = useState([]);
  const [candidateSearch, setCandidateSearch] = useState('');
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [addingPatientId, setAddingPatientId] = useState(null);
  const cancelRef = React.useRef();
  const cancelMarkPaidRef = React.useRef();
  const cancelUnmarkPaidRef = React.useRef();

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      // Fetch invoices with only needed fields (total_amount is computed dynamically, not stored)
      const { data: invoicesData, error: invError } = await supabase
        .from('submission_invoices')
        .select('id, invoice_number, original_batch_id, payment_status, created_at')
        .order('created_at', { ascending: false });

      if (invError) throw invError;
      setInvoices(invoicesData || []);

      const batchIds = [...new Set((invoicesData || []).map((i) => i.original_batch_id).filter(Boolean))];
      if (batchIds.length === 0) {
        setBatches({});
        setSubmissionsByBatch({});
        setLoading(false);
        return;
      }

      // Parallelize all dependent queries
      const [batchesResult, subsResult] = await Promise.all([
        supabase
          .from('submission_batches')
          .select('id, batch_name, description, status, ship_fee, created_at')
          .in('id', batchIds),
        selectAll(() => supabase
          .from('submissions')
          .select('id, batch_id, phlebotomist_id, lab_id, admin_fee, draw_fee_paid, invoice_price, patient_name')
          .in('batch_id', batchIds))
      ]);

      if (batchesResult.error) throw batchesResult.error;
      if (subsResult.error) throw subsResult.error;

      const batchesData = batchesResult.data || [];
      const subsData = subsResult.data || [];

      const batchMap = {};
      batchesData.forEach((b) => {
        batchMap[b.id] = b;
      });
      setBatches(batchMap);

      const subsByBatch = {};
      subsData.forEach((s) => {
        if (!subsByBatch[s.batch_id]) subsByBatch[s.batch_id] = [];
        subsByBatch[s.batch_id].push(s);
      });
      setSubmissionsByBatch(subsByBatch);

      // Extract unique IDs and fetch phlebotomists and labs in parallel
      const phlebIds = [...new Set(subsData.map((s) => s.phlebotomist_id).filter(Boolean))];
      const labIds = [...new Set(subsData.map((s) => s.lab_id).filter(Boolean))];

      const fetchPromises = [];
      if (phlebIds.length > 0) {
        fetchPromises.push(
          supabase
            .from('phlebotomist_profiles')
            .select('id, lab_draw_fee')
            .in('id', phlebIds)
            .then(({ data: phlebData, error: phlebError }) => {
              if (phlebError) {
                console.error('Error fetching phlebotomists:', phlebError);
                return;
              }
              const phlebMap = {};
              (phlebData || []).forEach((p) => {
                phlebMap[p.id] = p;
              });
              setPhlebotomists(phlebMap);
            })
            .catch(error => {
              console.error('Error in phlebotomist fetch promise:', error);
            })
        );
      }
      if (labIds.length > 0) {
        fetchPromises.push(
          supabase
            .from('labs')
            .select('id, name')
            .in('id', labIds)
            .then(({ data: labsData, error: labsError }) => {
              if (labsError) {
                console.error('Error fetching labs:', labsError);
                return;
              }
              const labMap = {};
              (labsData || []).forEach((l) => {
                labMap[l.id] = l;
              });
              setLabs(labMap);
            })
            .catch(error => {
              console.error('Error in labs fetch promise:', error);
            })
        );
      }

      await Promise.all(fetchPromises);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to fetch invoices. Check browser console (F12) for details.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Refetch when user switches to this tab so paid status is up to date (e.g. after recording manual payment on Submission Invoices)
  useEffect(() => {
    if (isActive) fetchInvoices();
  }, [isActive]);

  const computeAmount = (invoice) => {
    const batch = invoice.original_batch_id ? batches[invoice.original_batch_id] : null;
    if (!batch) return invoice.ship_fee ? parseFloat(invoice.ship_fee) : 0;
    const batchSubs = submissionsByBatch[batch.id] || [];
    const drawTotal = batchSubs.reduce((sum, sub) => sum + effectiveDrawPrice(sub), 0);
    const shipFee = batch.ship_fee ? parseFloat(batch.ship_fee) : 0;
    return drawTotal + shipFee;
  };

  const getClientName = (invoice) => {
    const batch = invoice.original_batch_id ? batches[invoice.original_batch_id] : null;
    if (batch?.batch_name) return batch.batch_name;
    const batchSubs = submissionsByBatch[batch?.id] || [];
    const firstLabId = batchSubs[0]?.lab_id;
    if (firstLabId && labs[firstLabId]) return labs[firstLabId].name;
    return '—';
  };

  const invoicesWithMeta = useMemo(() => {
    return (invoices || []).map((inv) => {
      const batch = inv.original_batch_id ? batches[inv.original_batch_id] : null;
      const batchSubs = batch ? (submissionsByBatch[batch.id] || []) : [];
      return {
        ...inv,
        _amount: computeAmount(inv),
        _clientName: getClientName(inv),
        _patientCount: batchSubs.length,
        _type: 'Batch',
      };
    });
  }, [invoices, batches, submissionsByBatch, phlebotomists, labs]);

  const filteredInvoices = useMemo(() => {
    let list = invoicesWithMeta;
    const q = (searchQuery || '').toLowerCase().trim();
    if (q) {
      list = list.filter(
        (inv) =>
          (inv.invoice_number && inv.invoice_number.toLowerCase().includes(q)) ||
          (inv._clientName && inv._clientName.toLowerCase().includes(q))
      );
    }
    if (statusFilter !== 'all') {
      const status = statusFilter;
      list = list.filter((inv) => (inv.payment_status || 'unpaid') === status);
    }
    return list;
  }, [invoicesWithMeta, searchQuery, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredInvoices.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedInvoices = filteredInvoices.slice(startIndex, startIndex + itemsPerPage);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        })
      : '—';

  const getStatusColor = (status) => getPaymentStatusColor(status);

  const handleView = (inv) => {
    setSelectedInvoice(inv);
    setViewSubmissions([]);
    setCandidatePatients([]);
    setCandidateSearch('');
    onViewOpen();
    loadInvoiceDetails(inv);
  };

  // Load full submission details for the modal
  const loadInvoiceDetails = async (inv) => {
    if (!inv?.original_batch_id) {
      setViewSubmissions([]);
      return;
    }
    setViewLoading(true);
    try {
      const { data: subs, error } = await supabase
        .from('submissions')
        .select('id, patient_name, patient_dob, drawn_at, phlebotomist_id, lab_id, admin_fee, draw_fee_paid, invoice_price, doctor_name, batch_id')
        .eq('batch_id', inv.original_batch_id)
        .order('blood_collection_time', { ascending: true });
      if (error) throw error;
      setViewSubmissions(subs || []);
    } catch (err) {
      toast({ title: 'Could not load patients', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setViewLoading(false);
    }
  };

  // Save a per-draw invoice price (floored at the $23 minimum) onto the blood file.
  const [savingPriceId, setSavingPriceId] = useState(null);
  const savePrice = async (sub, raw) => {
    const val = flooredPrice(raw);
    if (val === effectiveDrawPrice(sub)) return; // no change
    setSavingPriceId(sub.id);
    try {
      const { error } = await supabase.from('submissions').update({ invoice_price: val }).eq('id', sub.id);
      if (error) throw error;
      setViewSubmissions((prev) => prev.map((s) => (s.id === sub.id ? { ...s, invoice_price: val } : s)));
      setSubmissionsByBatch((prev) => {
        const bId = sub.batch_id;
        if (!bId || !prev[bId]) return prev;
        return { ...prev, [bId]: prev[bId].map((s) => (s.id === sub.id ? { ...s, invoice_price: val } : s)) };
      });
    } catch (e) {
      toast({ title: 'Could not update price', description: e.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSavingPriceId(null);
    }
  };

  // Safety check: refuse mutation if invoice has flipped to paid since modal opened
  const ensureInvoiceUnpaid = async (invoiceId) => {
    const { data, error } = await supabase
      .from('submission_invoices')
      .select('payment_status')
      .eq('id', invoiceId)
      .single();
    if (error) throw new Error('Could not verify invoice status');
    if ((data?.payment_status || 'unpaid') === 'paid') {
      throw new Error('Invoice is already paid — patients can no longer be changed');
    }
  };

  const removePatientFromInvoice = async (submission) => {
    if (!selectedInvoice) return;
    setRemovingPatientId(submission.id);
    try {
      await ensureInvoiceUnpaid(selectedInvoice.id);
      const { error } = await supabase
        .from('submissions')
        .update({ batch_id: null })
        .eq('id', submission.id);
      if (error) throw error;
      setViewSubmissions((prev) => prev.filter((s) => s.id !== submission.id));
      toast({ title: 'Patient removed', description: `${submission.patient_name || 'Patient'} removed from invoice.`, status: 'success', duration: 2500 });
      fetchInvoices();
    } catch (err) {
      toast({ title: 'Could not remove patient', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setRemovingPatientId(null);
    }
  };

  const searchCandidatePatients = async (query) => {
    if (!selectedInvoice) return;
    setCandidateLoading(true);
    try {
      // Scope to same lab as the current invoice (use first submission's lab_id)
      const labId = viewSubmissions.find((s) => s.lab_id)?.lab_id || null;
      const currentBatchId = selectedInvoice.original_batch_id;

      let q = supabase
        .from('submissions')
        .select('id, patient_name, patient_dob, blood_collection_time, lab_id, batch_id, draw_code')
        .neq('status', 'cancelled')
        .order('blood_collection_time', { ascending: false })
        .limit(50);

      if (labId) q = q.eq('lab_id', labId);
      // Exclude submissions already on this invoice's batch — but include those with no batch
      if (currentBatchId) q = q.or(`batch_id.is.null,batch_id.neq.${currentBatchId}`);
      if (query && query.trim()) q = q.ilike('patient_name', `%${query.trim()}%`);

      const { data, error } = await q;
      if (error) throw error;

      // Exclude submissions whose batch is already on a paid invoice
      const candidates = data || [];
      const otherBatchIds = [...new Set(candidates.map((c) => c.batch_id).filter(Boolean))];
      let paidBatchSet = new Set();
      if (otherBatchIds.length > 0) {
        const { data: paidInvs } = await supabase
          .from('submission_invoices')
          .select('original_batch_id')
          .in('original_batch_id', otherBatchIds)
          .eq('payment_status', 'paid');
        paidBatchSet = new Set((paidInvs || []).map((i) => i.original_batch_id));
      }
      const filtered = candidates.filter((c) => !c.batch_id || !paidBatchSet.has(c.batch_id));
      setCandidatePatients(filtered);
    } catch (err) {
      toast({ title: 'Search failed', description: err.message, status: 'error', duration: 3000 });
    } finally {
      setCandidateLoading(false);
    }
  };

  const addPatientToInvoice = async (submission) => {
    if (!selectedInvoice?.original_batch_id) return;
    setAddingPatientId(submission.id);
    try {
      await ensureInvoiceUnpaid(selectedInvoice.id);
      const { error } = await supabase
        .from('submissions')
        .update({ batch_id: selectedInvoice.original_batch_id })
        .eq('id', submission.id);
      if (error) throw error;
      // Refresh modal data
      await loadInvoiceDetails(selectedInvoice);
      setCandidatePatients((prev) => prev.filter((p) => p.id !== submission.id));
      toast({ title: 'Patient added', description: `${submission.patient_name || 'Patient'} added to invoice.`, status: 'success', duration: 2500 });
      fetchInvoices();
    } catch (err) {
      toast({ title: 'Could not add patient', description: err.message, status: 'error', duration: 4000, isClosable: true });
    } finally {
      setAddingPatientId(null);
    }
  };

  // Debounced candidate search when modal is open and user types
  useEffect(() => {
    if (!isViewOpen || !selectedInvoice) return;
    if (selectedInvoice.payment_status === 'paid') return;
    const t = setTimeout(() => { searchCandidatePatients(candidateSearch); }, 300);
    return () => clearTimeout(t);
  }, [candidateSearch, isViewOpen, selectedInvoice?.id, viewSubmissions.length]); // eslint-disable-line

  // Compute modal totals from viewSubmissions live
  const modalAmount = useMemo(() => {
    if (!selectedInvoice) return 0;
    const batch = selectedInvoice.original_batch_id ? batches[selectedInvoice.original_batch_id] : null;
    const shipFee = batch?.ship_fee ? parseFloat(batch.ship_fee) : 0;
    return viewSubmissions.reduce((sum, sub) => sum + effectiveDrawPrice(sub), shipFee);
  }, [viewSubmissions, selectedInvoice, batches]);

  const confirmMarkPaid = (inv) => {
    setInvoiceToMarkPaid(inv);
    onMarkPaidOpen();
  };

  const handleMarkPaid = async () => {
    if (!invoiceToMarkPaid) return;
    setMarkingPaidId(invoiceToMarkPaid.id);
    try {
      const { error } = await supabase
        .from('submission_invoices')
        .update({ payment_status: 'paid', updated_at: new Date().toISOString() })
        .eq('id', invoiceToMarkPaid.id);
      if (error) throw error;
      toast({
        title: 'Invoice marked paid',
        description: `Invoice ${invoiceToMarkPaid.invoice_number || invoiceToMarkPaid.id} updated.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchInvoices();
      onMarkPaidClose();
      setInvoiceToMarkPaid(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update invoice',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setMarkingPaidId(null);
    }
  };

  const confirmUnmarkPaid = (inv) => {
    setInvoiceToUnmarkPaid(inv);
    onUnmarkPaidOpen();
  };

  const handleUnmarkPaid = async () => {
    if (!invoiceToUnmarkPaid) return;
    setUnmarkingPaidId(invoiceToUnmarkPaid.id);
    try {
      const { error } = await supabase
        .from('submission_invoices')
        .update({ payment_status: 'unpaid', updated_at: new Date().toISOString() })
        .eq('id', invoiceToUnmarkPaid.id);
      if (error) throw error;
      toast({
        title: 'Payment status reverted',
        description: `Invoice ${invoiceToUnmarkPaid.invoice_number || invoiceToUnmarkPaid.id} marked as unpaid.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchInvoices();
      onUnmarkPaidClose();
      setInvoiceToUnmarkPaid(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update invoice',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setUnmarkingPaidId(null);
    }
  };

  const confirmDelete = (inv) => {
    setInvoiceToDelete(inv);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!invoiceToDelete) return;
    setDeletingId(invoiceToDelete.id);
    try {
      const { error: unlinkError } = await supabase
        .from('submissions')
        .update({ invoice_id: null })
        .eq('invoice_id', invoiceToDelete.id);
      if (unlinkError) throw unlinkError;
      const { error: delError } = await supabase
        .from('submission_invoices')
        .delete()
        .eq('id', invoiceToDelete.id);
      if (delError) throw delError;
      toast({
        title: 'Invoice deleted',
        description: `Invoice ${invoiceToDelete.invoice_number || invoiceToDelete.id} removed.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchInvoices();
      onDeleteClose();
      setInvoiceToDelete(null);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete invoice',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Container maxW="full" py={6}>
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <Flex align="center">
            <Icon as={FaFileInvoiceDollar} boxSize={6} color="blue.500" mr={3} />
            <Heading size="lg" color="gray.800">
              Invoices
            </Heading>
          </Flex>
        </Flex>

        <Card boxShadow="sm" borderRadius="lg">
          <CardBody p={4}>
            <HStack spacing={4} wrap="wrap">
              <InputGroup size="md" maxW="400px" flex={1} minW="250px">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Search by invoice number or client name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px #3182ce" }}
                />
              </InputGroup>
              <Select
                size="md"
                w="180px"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                icon={<ChevronDownIcon />}
                bg="white"
                borderColor="gray.300"
                _hover={{ borderColor: "gray.400" }}
              >
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
              </Select>
            </HStack>
          </CardBody>
        </Card>

        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <Text color="gray.600" fontSize="sm" fontWeight="medium">
            Showing <Text as="span" fontWeight="bold" color="gray.800">{paginatedInvoices.length}</Text> of{' '}
            <Text as="span" fontWeight="bold" color="gray.800">{filteredInvoices.length}</Text> invoices
            {filteredInvoices.length !== invoices.length && (
              <Text as="span" color="gray.500">
                {' '}(filtered from {invoices.length} total)
              </Text>
            )}
          </Text>
          <HStack spacing={2}>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
            >
              Previous
            </Button>
            <Text fontSize="sm" fontWeight="medium" color="gray.700" px={3}>
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              size="sm"
              variant="outline"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
            >
              Next
            </Button>
          </HStack>
        </Flex>

        <Card boxShadow="sm" borderRadius="lg" overflow="hidden">
          <CardBody p={0}>
            <TableContainer>
              <Table size="md" variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th fontWeight="semibold" color="gray.700" py={4} fontSize="sm">Invoice Number</Th>
                    <Th fontWeight="semibold" color="gray.700" py={4} fontSize="sm">Lab / Client Name</Th>
                    <Th fontWeight="semibold" color="gray.700" py={4} fontSize="sm">Type</Th>
                    <Th fontWeight="semibold" color="gray.700" py={4} fontSize="sm" isNumeric>Patients</Th>
                    <Th fontWeight="semibold" color="gray.700" py={4} fontSize="sm" isNumeric>Amount</Th>
                    <Th fontWeight="semibold" color="gray.700" py={4} fontSize="sm">Status</Th>
                    <Th fontWeight="semibold" color="gray.700" py={4} fontSize="sm">Date Created</Th>
                    <Th fontWeight="semibold" color="gray.700" py={4} fontSize="sm">Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {loading ? (
                    <Tr>
                      <Td colSpan={8} textAlign="center" py={12}>
                        <VStack spacing={4}>
                          <Spinner size="lg" color="blue.500" thickness="4px" />
                          <Text color="gray.600" fontWeight="medium">Loading invoices...</Text>
                        </VStack>
                      </Td>
                    </Tr>
                  ) : paginatedInvoices.length === 0 ? (
                    <Tr>
                      <Td colSpan={8} textAlign="center" py={12}>
                        <VStack spacing={3}>
                          <Icon as={FaFileInvoiceDollar} boxSize={12} color="gray.300" />
                          <Text color="gray.500" fontSize="md" fontWeight="medium">No invoices found</Text>
                          {searchQuery && (
                            <Text color="gray.400" fontSize="sm">
                              Try adjusting your search or filter criteria
                            </Text>
                          )}
                        </VStack>
                      </Td>
                    </Tr>
                  ) : (
                    paginatedInvoices.map((inv, index) => (
                      <Tr
                        key={inv.id}
                        _hover={{ bg: 'gray.50' }}
                        bg={index % 2 === 0 ? 'white' : 'gray.25'}
                        transition="background 0.2s"
                      >
                        <Td py={4}>
                          <Text fontSize="sm" fontWeight="bold" color="gray.800">
                            {inv.invoice_number || '—'}
                          </Text>
                        </Td>
                        <Td py={4}>
                          <Text fontSize="sm" color="gray.700">
                            {inv._clientName}
                          </Text>
                        </Td>
                        <Td py={4}>
                          <Badge colorScheme="blue" variant="subtle" fontSize="xs" px={2} py={1}>
                            {inv._type}
                          </Badge>
                        </Td>
                        <Td py={4} isNumeric>
                          <Text fontSize="sm" fontWeight="bold" color="gray.700">
                            {inv._patientCount || 0}
                          </Text>
                        </Td>
                        <Td py={4} isNumeric>
                          <Text fontSize="sm" fontWeight="bold" color="green.600">
                            {formatCurrency(inv._amount)}
                          </Text>
                        </Td>
                        <Td py={4}>
                          <Badge 
                            colorScheme={getStatusColor(inv.payment_status || 'unpaid')} 
                            fontSize="xs"
                            px={2}
                            py={1}
                            variant={inv.payment_status === 'paid' ? 'solid' : 'subtle'}
                          >
                            {(inv.payment_status || 'unpaid').toUpperCase()}
                          </Badge>
                        </Td>
                        <Td py={4}>
                          <Text fontSize="xs" color="gray.600">
                            {formatDate(inv.created_at)}
                          </Text>
                        </Td>
                        <Td py={4}>
                          <HStack spacing={2} flexWrap="wrap">
                            <Button
                              size="sm"
                              variant="outline"
                              leftIcon={<FaEye />}
                              onClick={() => handleView(inv)}
                              fontSize="xs"
                            >
                              View
                            </Button>
                            {(inv.payment_status || 'unpaid') !== 'paid' ? (
                              <Button
                                size="sm"
                                leftIcon={<FaCheckCircle />}
                                colorScheme="green"
                                onClick={() => confirmMarkPaid(inv)}
                                isLoading={markingPaidId === inv.id}
                                fontSize="xs"
                                fontWeight="semibold"
                              >
                                Mark Paid
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                leftIcon={<FaUndo />}
                                colorScheme="orange"
                                variant="outline"
                                onClick={() => confirmUnmarkPaid(inv)}
                                fontSize="xs"
                                fontWeight="semibold"
                              >
                                Unmark Paid
                              </Button>
                            )}
                            <IconButton
                              aria-label="Delete invoice"
                              icon={<DeleteIcon />}
                              size="sm"
                              variant="ghost"
                              colorScheme="red"
                              onClick={() => confirmDelete(inv)}
                              _hover={{ bg: 'red.50' }}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      </VStack>

      <Modal isOpen={isViewOpen} onClose={onViewClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedInvoice && (
              <Flex justify="space-between" align="center" pr={6} gap={3} flexWrap="wrap">
                <Box>
                  <Text fontSize="lg" fontWeight="800">Invoice {selectedInvoice.invoice_number || selectedInvoice.id}</Text>
                  <Text fontSize="sm" color="gray.500" fontWeight="500">{selectedInvoice._clientName} · {formatDate(selectedInvoice.created_at)}</Text>
                </Box>
                <Badge colorScheme={getStatusColor(selectedInvoice.payment_status || 'unpaid')} fontSize="sm" px={3} py={1} borderRadius="full">
                  {(selectedInvoice.payment_status || 'unpaid').toUpperCase()}
                </Badge>
              </Flex>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedInvoice && (() => {
              const isPaid = (selectedInvoice.payment_status || 'unpaid') === 'paid';
              return (
                <VStack align="stretch" spacing={5}>
                  {isPaid && (
                    <HStack bg="orange.50" border="1px" borderColor="orange.200" p={3} borderRadius="md" spacing={2}>
                      <Icon as={FaLock} color="orange.500" />
                      <Box>
                        <Text fontSize="sm" fontWeight="700" color="orange.700">This invoice is paid — patients are locked</Text>
                        <Text fontSize="xs" color="orange.600">Unmark as paid to edit, or create an adjustment invoice.</Text>
                      </Box>
                    </HStack>
                  )}

                  {/* Summary stats */}
                  <HStack spacing={8} flexWrap="wrap">
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="700" textTransform="uppercase" letterSpacing="0.5px">Patients</Text>
                      <Text fontSize="2xl" fontWeight="800" color="gray.800">{viewSubmissions.length}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500" fontWeight="700" textTransform="uppercase" letterSpacing="0.5px">Total</Text>
                      <Text fontSize="2xl" fontWeight="800" color="green.600">{formatCurrency(modalAmount)}</Text>
                    </Box>
                  </HStack>

                  {/* Patients table */}
                  <Box>
                    <Heading size="sm" mb={2} color="gray.700">Patients on this invoice</Heading>
                    {viewLoading ? (
                      <Flex py={6} justify="center"><Spinner color="blue.500" /></Flex>
                    ) : viewSubmissions.length === 0 ? (
                      <Box py={6} textAlign="center" bg="gray.50" borderRadius="md">
                        <Text fontSize="sm" color="gray.500">No patients on this invoice.</Text>
                      </Box>
                    ) : (
                      <TableContainer border="1px" borderColor="gray.200" borderRadius="md">
                        <Table size="sm">
                          <Thead bg="gray.50">
                            <Tr>
                              <Th>Patient</Th>
                              <Th>Draw Date</Th>
                              <Th isNumeric>Price</Th>
                              {!isPaid && <Th></Th>}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {viewSubmissions.map((sub) => {
                              return (
                                <Tr key={sub.id}>
                                  <Td fontWeight="600">{sub.patient_name || 'Patient'}</Td>
                                  <Td>{formatDate(sub.drawn_at)}</Td>
                                  <Td isNumeric fontWeight="700">
                                    {isPaid ? (
                                      formatCurrency(effectiveDrawPrice(sub))
                                    ) : (
                                      <Input
                                        size="xs"
                                        type="number"
                                        min={MIN_DRAW_PRICE}
                                        step="1"
                                        textAlign="right"
                                        w="84px"
                                        ml="auto"
                                        defaultValue={effectiveDrawPrice(sub)}
                                        isDisabled={savingPriceId === sub.id}
                                        onBlur={(e) => savePrice(sub, e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                      />
                                    )}
                                  </Td>
                                  {!isPaid && (
                                    <Td>
                                      <IconButton
                                        aria-label="Remove patient"
                                        icon={<FaTrash />}
                                        size="xs"
                                        colorScheme="red"
                                        variant="ghost"
                                        isLoading={removingPatientId === sub.id}
                                        onClick={() => removePatientFromInvoice(sub)}
                                      />
                                    </Td>
                                  )}
                                </Tr>
                              );
                            })}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>

                  {/* Add patient section — unpaid only */}
                  {!isPaid && (
                    <Box bg="blue.50" border="1px" borderColor="blue.100" p={4} borderRadius="md">
                      <HStack mb={3}>
                        <Icon as={FaUserPlus} color="blue.600" />
                        <Heading size="sm" color="blue.700">Add a patient</Heading>
                      </HStack>
                      <Text fontSize="xs" color="gray.600" mb={2}>
                        Showing recent patients{viewSubmissions.find((s) => s.lab_id) ? ' for the same lab' : ''} that aren't already on this invoice or any paid invoice. Type to filter by name.
                      </Text>
                      <InputGroup size="sm" mb={3}>
                        <InputLeftElement pointerEvents="none">
                          <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                          placeholder="Search patient name..."
                          value={candidateSearch}
                          onChange={(e) => setCandidateSearch(e.target.value)}
                          bg="white"
                        />
                      </InputGroup>
                      {candidateLoading ? (
                        <Flex py={3} justify="center"><Spinner size="sm" color="blue.500" /></Flex>
                      ) : candidatePatients.length === 0 ? (
                        <Text fontSize="xs" color="gray.500" textAlign="center" py={2}>
                          {candidateSearch ? 'No matching patients found.' : 'No available patients to add.'}
                        </Text>
                      ) : (
                        <VStack align="stretch" spacing={1} maxH="240px" overflowY="auto">
                          {candidatePatients.map((p) => (
                            <Flex
                              key={p.id}
                              bg="white"
                              p={2}
                              borderRadius="md"
                              justify="space-between"
                              align="center"
                              border="1px"
                              borderColor="gray.100"
                            >
                              <Box flex={1} minW={0}>
                                <HStack spacing={2}>
                                  <Text fontSize="sm" fontWeight="600" noOfLines={1}>{p.patient_name || 'Patient'}</Text>
                                  {p.batch_id ? (
                                    <Badge colorScheme="orange" fontSize="2xs">In another batch</Badge>
                                  ) : (
                                    <Badge colorScheme="green" fontSize="2xs">Unbatched</Badge>
                                  )}
                                </HStack>
                                <HStack spacing={2} fontSize="xs" color="gray.500">
                                  <Text>{formatDate(p.blood_collection_time)}</Text>
                                  {p.draw_code && <Text fontFamily="mono">#{p.draw_code}</Text>}
                                </HStack>
                              </Box>
                              <Button
                                size="xs"
                                colorScheme="blue"
                                leftIcon={<AddIcon />}
                                isLoading={addingPatientId === p.id}
                                onClick={() => addPatientToInvoice(p)}
                              >
                                Add
                              </Button>
                            </Flex>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  )}
                </VStack>
              );
            })()}
          </ModalBody>
          <ModalFooter>
            <Button
              leftIcon={<FaFilePdf />}
              colorScheme="red"
              variant="outline"
              mr={2}
              onClick={() => downloadInvoicePDF({
                invoice: selectedInvoice,
                clientName: selectedInvoice?._clientName,
                subs: viewSubmissions,
              })}
            >
              PDF
            </Button>
            <Button
              leftIcon={<FaFileCsv />}
              colorScheme="green"
              variant="outline"
              mr={2}
              onClick={() => downloadText(
                `invoice-${selectedInvoice?.invoice_number || 'export'}.csv`,
                buildInvoiceCSV({ invoice: selectedInvoice, clientName: selectedInvoice?._clientName, subs: viewSubmissions }),
              )}
            >
              CSV
            </Button>
            <Button variant="ghost" onClick={onViewClose} mr={2}>
              Close
            </Button>
            {selectedInvoice?.original_batch_id && (
              <Button
                colorScheme="blue"
                variant="outline"
                onClick={() => {
                  onViewClose();
                  navigate(`/admin/invoices?batch=${selectedInvoice.original_batch_id}`);
                }}
              >
                Open in Batches
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
        <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>Delete Invoice</AlertDialogHeader>
          <AlertDialogBody>
            Are you sure you want to delete invoice {invoiceToDelete?.invoice_number || invoiceToDelete?.id}?
            Submissions will be unlinked from this invoice.
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onDeleteClose}>
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={handleDelete}
              isLoading={deletingId !== null}
              ml={3}
            >
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog isOpen={isMarkPaidOpen} leastDestructiveRef={cancelMarkPaidRef} onClose={onMarkPaidClose}>
        <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>Mark Invoice as Paid</AlertDialogHeader>
          <AlertDialogBody>
            Are you sure you want to mark invoice <strong>{invoiceToMarkPaid?.invoice_number || invoiceToMarkPaid?.id}</strong> as paid?
            <br />
            <br />
            This will update the payment status to "paid". Make sure payment has been received before confirming.
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelMarkPaidRef} onClick={onMarkPaidClose}>
              Cancel
            </Button>
            <Button
              colorScheme="green"
              onClick={handleMarkPaid}
              isLoading={markingPaidId !== null}
              ml={3}
            >
              Mark as Paid
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog isOpen={isUnmarkPaidOpen} leastDestructiveRef={cancelUnmarkPaidRef} onClose={onUnmarkPaidClose}>
        <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>Unmark Invoice as Paid</AlertDialogHeader>
          <AlertDialogBody>
            Are you sure you want to mark invoice <strong>{invoiceToUnmarkPaid?.invoice_number || invoiceToUnmarkPaid?.id}</strong> as unpaid?
            <br />
            <br />
            This will revert the payment status to "unpaid". Use this if the invoice was marked paid by mistake or if payment needs to be re-processed.
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelUnmarkPaidRef} onClick={onUnmarkPaidClose}>
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              onClick={handleUnmarkPaid}
              isLoading={unmarkingPaidId !== null}
              ml={3}
            >
              Unmark as Paid
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default InvoicesList;
